import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';
import Flyout from '@/components/common/Flyout';
import { FlyoutMenuItem } from '@/components/common/Flyout/item';
import { usePickMediaFile } from '@/hooks/files/use-pick-media-file';
import useUserPlatform from '@/hooks/use-user-platform';
import useUploadHeadImage from '@/hooks/users/use-update-header-image';
import { FileType } from '@/utils/file-type';

interface Props {
  disable?: boolean;
}

export function HeadImageUploadButton({ disable, children }: PropsWithChildren<Props>) {
  const { isIOS, isEmbedded } = useUserPlatform();
  const [show, , setShow] = useToggle();
  const { ref, pickMedia } = usePickMediaFile();
  const { upload: updateHeadImg, isLoading } = useUploadHeadImage();
  const { t } = useTranslation();

  return (
    <>
      <button
        type='button'
        disabled={disable || isEmbedded}
        data-loading={isLoading}
        className='group relative'
        onClick={() => (isIOS ? pickMedia(undefined, FileType.Image) : setShow(true))}>
        <span className='group-data-[loading=true]:blur'>{children}</span>

        <span className='loading loading-spinner loading-sm absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 text-secondary group-data-[loading=true]:inline' />
      </button>

      <input
        type='file'
        className='sr-only'
        ref={ref}
        value=''
        onChange={(e) => {
          if (e.target.files?.[0]) {
            updateHeadImg(e.target.files[0]).catch(console.error);
          }
          setShow(false);
        }}
      />

      <Flyout
        className='mx-2'
        show={show}
        onClose={() => setShow(false)}>
        <div className='flex flex-col items-stretch gap-2'>
          <div className='join join-vertical gap-y-px rounded-system-button'>
            {(['album', 'camera'] as const).map((item) => (
              <FlyoutMenuItem
                joined
                key={item}
                label={t(`upload.${item}`)}
                onClick={() => pickMedia(item, FileType.Image)}
              />
            ))}
          </div>
          <FlyoutMenuItem
            className='font-bold'
            label={t('common.cancel')}
            onClick={() => setShow(false)}
          />
        </div>
      </Flyout>
    </>
  );
}
